'use client';

import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { TextReveal } from './primitives/TextReveal';
import { LuxuryIcon, type IconName } from './icons/LuxuryIcons';

/**
 * PortfolioTracker — "Your closet is a portfolio."
 *
 * A live, editable mock dashboard where the user taps pieces to add them.
 * Each piece has retail + current resale value. Totals animate via
 * spring-backed motion values. A CSS-only donut chart shows allocation
 * by retention tier (trophy / stable / drift).
 *
 * Coming Q4 2026 — we lead with the ribbon.
 */

interface Piece {
  id: string;
  brand: string;
  model: string;
  icon: IconName;
  retail: number;
  retention: number; // 0..1.1+ (appreciation possible)
}

const pieces: Piece[] = [
  { id: 'birkin25', brand: 'Hermès', model: 'Birkin 25', icon: 'handbag', retail: 65000, retention: 0.95 },
  { id: 'sub', brand: 'Rolex', model: 'Submariner', icon: 'watch', retail: 58000, retention: 1.12 },
  { id: 'kelly', brand: 'Hermès', model: 'Kelly 28', icon: 'handbag', retail: 72000, retention: 0.9 },
  { id: 'cf', brand: 'Chanel', model: 'Classic Flap', icon: 'wallet', retail: 42000, retention: 0.88 },
  { id: 'daytona', brand: 'Rolex', model: 'Daytona', icon: 'watch', retail: 82000, retention: 1.35 },
  { id: 'nautilus', brand: 'Patek', model: 'Nautilus 5711', icon: 'watch', retail: 140000, retention: 1.8 },
  { id: 'boy', brand: 'Chanel', model: 'Boy Bag', icon: 'wallet', retail: 28000, retention: 0.72 },
  { id: 'marmont', brand: 'Gucci', model: 'GG Marmont', icon: 'handbag', retail: 9400, retention: 0.45 },
  { id: 'never', brand: 'LV', model: 'Neverfull MM', icon: 'handbag', retail: 8400, retention: 0.6 },
];

function Counter({ value, prefix = '', suffix = '' }: { value: number; prefix?: string; suffix?: string }) {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { damping: 40, stiffness: 100, mass: 0.8 });
  const rounded = useTransform(spring, (v) => Math.round(v));
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    mv.set(value);
    const unsub = rounded.on('change', (v) => setDisplay(v));
    return unsub;
  }, [value, mv, rounded]);

  return (
    <span>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

const STORAGE_KEY = 'sophia_portfolio_v1';
const EMAIL_KEY = 'sophia_portfolio_email';
const API_BASE = 'https://api.mysophia.tech';

function loadInitial(): Set<string> {
  if (typeof window === 'undefined') return new Set(['birkin25', 'sub', 'cf']);
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set(['birkin25', 'sub', 'cf']);
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set(['birkin25', 'sub', 'cf']);
    return new Set(arr.filter((s) => typeof s === 'string'));
  } catch {
    return new Set(['birkin25', 'sub', 'cf']);
  }
}

function loadEmail(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}

type SyncStatus = 'idle' | 'loading' | 'synced' | 'saving' | 'error';

export function PortfolioTracker() {
  const { locale } = useLocale();
  const isAr = locale === 'ar';
  const [selected, setSelected] = useState<Set<string>>(loadInitial);
  const [syncEmail, setSyncEmail] = useState<string>('');     // bound to input
  const [activeEmail, setActiveEmail] = useState<string>(''); // email currently syncing
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');

  // Hydrate email from localStorage once — and if present, kick off a
  // load from the server so we come back to the same portfolio on a
  // new device after a user enters the same email.
  useEffect(() => {
    const stored = loadEmail();
    if (stored) {
      setSyncEmail(stored);
      setActiveEmail(stored);
      void loadFromServer(stored, setSelected, setSyncStatus);
    }
  }, []);

  // Mirror every change locally, AND push to server if we have an active email.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(selected)));
    } catch {
      /* private mode / quota exceeded */
    }
    if (activeEmail) {
      void saveToServer(activeEmail, selected, locale, setSyncStatus);
    }
  }, [selected, activeEmail, locale]);

  const copy = isAr
    ? {
        ribbon: 'مباشر',
        syncPrompt: 'أضف بريدك لمزامنة محفظتك عبر الأجهزة',
        syncPlaceholder: 'your@email.com',
        syncEnable: 'فعّل المزامنة',
        syncDisable: 'إيقاف المزامنة',
        syncing: 'جارٍ الحفظ…',
        synced: 'مزامنة كـ',
        syncFailed: 'فشل الاتصال',
        localOnly: 'محفوظ على جهازك فقط',
        eyebrow: 'متعقّب المحفظة',
        heading: 'خزانتك محفظة.',
        sub: 'أضف قطعك — Sophia تتبع القيمة الإجمالية، الاحتفاظ، والأفضل أداءً كما تفعل مع أسهمك.',
        total: 'القيمة الإجمالية',
        retail: 'السعر الأصلي',
        retention: 'الاحتفاظ',
        best: 'الأفضل أداءً',
        pieces: 'قطعة',
        addPrompt: 'انقر على أي قطعة لإضافتها',
        trophy: 'استثمارية',
        stable: 'مستقرة',
        drift: 'منخفضة',
      }
    : {
        ribbon: 'Live',
        syncPrompt: 'Add your email to sync across devices',
        syncPlaceholder: 'your@email.com',
        syncEnable: 'Enable sync',
        syncDisable: 'Stop syncing',
        syncing: 'Saving…',
        synced: 'Synced as',
        syncFailed: 'Connection failed',
        localOnly: 'Saved on this device only',
        eyebrow: 'Portfolio tracker',
        heading: 'Your closet is a portfolio.',
        sub: 'Add pieces and Sophia tracks total value, retention, and best-performers — like a broker statement for your wardrobe.',
        total: 'Total value',
        retail: 'Retail paid',
        retention: 'Retention',
        best: 'Best performer',
        pieces: 'pieces',
        addPrompt: 'Tap a piece to add it',
        trophy: 'Trophy',
        stable: 'Stable',
        drift: 'Drifting',
      };

  const selectedPieces = pieces.filter((p) => selected.has(p.id));

  const totals = useMemo(() => {
    const retail = selectedPieces.reduce((s, p) => s + p.retail, 0);
    const current = selectedPieces.reduce((s, p) => s + p.retail * p.retention, 0);
    const best = selectedPieces
      .slice()
      .sort((a, b) => b.retention - a.retention)[0];
    // Tier breakdown for donut
    const trophy = selectedPieces
      .filter((p) => p.retention >= 0.85)
      .reduce((s, p) => s + p.retail * p.retention, 0);
    const stable = selectedPieces
      .filter((p) => p.retention >= 0.65 && p.retention < 0.85)
      .reduce((s, p) => s + p.retail * p.retention, 0);
    const drift = selectedPieces
      .filter((p) => p.retention < 0.65)
      .reduce((s, p) => s + p.retail * p.retention, 0);
    return { retail, current, best, trophy, stable, drift };
  }, [selectedPieces]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const total = totals.current || 1;
  const trophyPct = (totals.trophy / total) * 100;
  const stablePct = (totals.stable / total) * 100;
  const driftPct = (totals.drift / total) * 100;

  const retentionPct =
    totals.retail > 0 ? Math.round((totals.current / totals.retail) * 100) : 0;

  return (
    <section className="relative py-12 sm:py-16 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold-soft font-semibold">
              {copy.eyebrow}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-emerald/15 text-emerald-soft text-[9px] tracking-[0.25em] uppercase font-bold border border-emerald/20">
              {copy.ribbon}
            </span>
          </div>
          <TextReveal as="h2" className="font-serif text-display-sm text-cream leading-[0.98]">
            {copy.heading}
          </TextReveal>
          <p className="mt-6 text-fog-300 text-lg leading-relaxed max-w-xl">{copy.sub}</p>
        </div>

        {/* Sync bar — email-as-identity cross-device sync */}
        <div className="mb-6 rounded-luxe bg-obsidian-700/40 border border-white/5 p-4 flex items-center gap-3 flex-wrap" dir={isAr ? 'rtl' : 'ltr'}>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] tracking-[0.25em] uppercase text-gold-soft font-semibold mb-1">
              {activeEmail ? `${copy.synced} ${activeEmail}` : copy.syncPrompt}
            </div>
            <div className="text-[11px] text-fog-500">
              {syncStatus === 'loading' || syncStatus === 'saving' ? copy.syncing
                : syncStatus === 'error' ? copy.syncFailed
                : activeEmail ? null
                : copy.localOnly}
            </div>
          </div>
          {activeEmail ? (
            <button
              onClick={() => {
                setActiveEmail('');
                setSyncEmail('');
                try { window.localStorage.removeItem(EMAIL_KEY); } catch { /* ignore */ }
                setSyncStatus('idle');
              }}
              className="px-4 py-2 rounded-pill bg-obsidian-900 text-cream text-xs font-semibold border border-white/10 hover:border-white/25 transition"
            >
              {copy.syncDisable}
            </button>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const email = syncEmail.trim().toLowerCase();
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                  setSyncStatus('error');
                  return;
                }
                try { window.localStorage.setItem(EMAIL_KEY, email); } catch { /* ignore */ }
                setActiveEmail(email);
                await loadFromServer(email, setSelected, setSyncStatus);
              }}
              className="flex gap-2"
              dir="ltr"
            >
              <input
                type="email"
                value={syncEmail}
                onChange={(e) => setSyncEmail(e.target.value)}
                placeholder={copy.syncPlaceholder}
                className="px-3 py-2 rounded-pill bg-obsidian-900 border border-white/10 text-cream text-xs placeholder:text-fog-500 focus:border-gold/40 focus:outline-none"
              />
              <button
                type="submit"
                className="px-4 py-2 rounded-pill bg-gold-gradient text-obsidian-900 text-xs font-bold hover:shadow-glow-gold transition"
              >
                {copy.syncEnable}
              </button>
            </form>
          )}
        </div>

        <div className="grid lg:grid-cols-[1.15fr,1fr] gap-6 lg:gap-8 items-start" dir="ltr">
          {/* Left: pieces grid */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-fog-500 font-semibold">
                {copy.addPrompt}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {pieces.map((p) => {
                const isOn = selected.has(p.id);
                const appreciating = p.retention >= 1;
                return (
                  <button
                    key={p.id}
                    onClick={() => toggle(p.id)}
                    data-cursor="cta"
                    className={`group text-start rounded-luxe p-4 border transition-all ${
                      isOn
                        ? 'bg-gold/10 border-gold/40 shadow-glow-gold'
                        : 'bg-obsidian-700/50 border-white/5 hover:border-white/15 hover:bg-obsidian-700'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <LuxuryIcon
                        kind={p.icon}
                        size={36}
                        className={`transition ${
                          isOn ? 'text-gold-soft' : 'text-fog-500 group-hover:text-fog-300'
                        }`}
                      />
                      <motion.span
                        animate={{ scale: isOn ? 1 : 0.85, opacity: isOn ? 1 : 0.3 }}
                        className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold ${
                          isOn
                            ? 'bg-gold text-obsidian-900'
                            : 'bg-white/5 text-fog-500'
                        }`}
                      >
                        {isOn ? '✓' : '+'}
                      </motion.span>
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-fog-500">
                      {p.brand}
                    </div>
                    <div className="text-sm text-cream font-medium mb-1">{p.model}</div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-fog-300">AED {p.retail.toLocaleString()}</span>
                      <span
                        className={`font-mono font-bold ${
                          appreciating ? 'text-emerald-soft' : 'text-fog-500'
                        }`}
                      >
                        {appreciating ? '↑' : '↓'} {Math.round(p.retention * 100)}%
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/*
           * Right: live totals card.
           * Sticky only at lg+ — on mobile the grid stacks this card
           * ABOVE the piece grid wouldn't make sense, so it sits below
           * and we let it scroll with the page like a normal block.
           */}
          <div className="lg:sticky lg:top-24">
            <motion.div
              layout
              className="glass rounded-luxe p-6"
            >
              {/* Total value */}
              <div className="mb-5">
                <div className="text-[10px] tracking-[0.25em] uppercase text-gold-soft font-semibold mb-2">
                  {copy.total}
                </div>
                <div className="font-serif text-5xl text-cream font-bold leading-none tabular-nums">
                  <Counter value={Math.round(totals.current)} prefix="AED " />
                </div>
                <div className="mt-2 flex items-baseline gap-3 text-xs text-fog-500">
                  <span>
                    {copy.retail}:{' '}
                    <span className="text-fog-300 font-mono">
                      AED {totals.retail.toLocaleString()}
                    </span>
                  </span>
                  <span className={retentionPct >= 85 ? 'text-emerald-soft font-semibold' : 'text-fog-500'}>
                    {retentionPct}% {copy.retention.toLowerCase()}
                  </span>
                </div>
              </div>

              {/* Donut chart */}
              {selectedPieces.length > 0 && (
                <div className="mb-5 flex items-center gap-5">
                  <div className="relative w-24 h-24 shrink-0">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
                      {trophyPct > 0 && (
                        <motion.circle
                          cx="50"
                          cy="50"
                          r="42"
                          fill="none"
                          stroke="#c9a96a"
                          strokeWidth="10"
                          initial={{ strokeDasharray: '0 264' }}
                          animate={{
                            strokeDasharray: `${(trophyPct / 100) * 264} 264`,
                          }}
                          transition={{ duration: 1, ease: 'easeOut' }}
                          strokeLinecap="butt"
                        />
                      )}
                      {stablePct > 0 && (
                        <motion.circle
                          cx="50"
                          cy="50"
                          r="42"
                          fill="none"
                          stroke="#2a9d7b"
                          strokeWidth="10"
                          initial={{ strokeDasharray: '0 264' }}
                          animate={{
                            strokeDasharray: `${(stablePct / 100) * 264} 264`,
                            strokeDashoffset: -((trophyPct / 100) * 264),
                          }}
                          transition={{ duration: 1, ease: 'easeOut', delay: 0.1 }}
                          strokeLinecap="butt"
                        />
                      )}
                      {driftPct > 0 && (
                        <motion.circle
                          cx="50"
                          cy="50"
                          r="42"
                          fill="none"
                          stroke="#d4756a"
                          strokeWidth="10"
                          initial={{ strokeDasharray: '0 264' }}
                          animate={{
                            strokeDasharray: `${(driftPct / 100) * 264} 264`,
                            strokeDashoffset: -(((trophyPct + stablePct) / 100) * 264),
                          }}
                          transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
                          strokeLinecap="butt"
                        />
                      )}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center font-mono text-xs text-cream">
                      {selectedPieces.length}
                    </div>
                  </div>
                  <div className="flex-1 space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-fog-300">
                        <span className="w-2 h-2 rounded-full bg-gold" /> {copy.trophy}
                      </span>
                      <span className="font-mono text-cream">{Math.round(trophyPct)}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-fog-300">
                        <span className="w-2 h-2 rounded-full bg-emerald-soft" /> {copy.stable}
                      </span>
                      <span className="font-mono text-cream">{Math.round(stablePct)}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-fog-300">
                        <span className="w-2 h-2 rounded-full bg-rose" /> {copy.drift}
                      </span>
                      <span className="font-mono text-cream">{Math.round(driftPct)}%</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Best performer */}
              <AnimatePresence mode="wait">
                {totals.best && (
                  <motion.div
                    key={totals.best.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="rounded-luxe bg-gold/10 border border-gold/20 p-4"
                  >
                    <div className="text-[10px] tracking-[0.25em] uppercase text-gold-soft font-semibold mb-1">
                      {copy.best}
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-cream">
                          {totals.best.brand} {totals.best.model}
                        </div>
                        <div className="text-xs text-fog-500">
                          AED {totals.best.retail.toLocaleString()} retail
                        </div>
                      </div>
                      <div className="font-mono font-bold text-emerald-soft text-sm">
                        {totals.best.retention >= 1 ? '↑' : '↓'}{' '}
                        {Math.round(totals.best.retention * 100)}%
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Sync helpers ----------

async function loadFromServer(
  email: string,
  setSelected: (s: Set<string>) => void,
  setStatus: (s: SyncStatus) => void,
): Promise<void> {
  setStatus('loading');
  try {
    const res = await fetch(`${API_BASE}/api/portfolio/${encodeURIComponent(email)}`);
    const json = (await res.json()) as { success: boolean; data?: { pieces: Array<{ sku: string }> } };
    if (!res.ok || !json.success) {
      setStatus('error');
      return;
    }
    const skus = (json.data?.pieces ?? []).map((p) => p.sku).filter(Boolean);
    if (skus.length > 0) {
      setSelected(new Set(skus));
    }
    setStatus('synced');
  } catch {
    setStatus('error');
  }
}

async function saveToServer(
  email: string,
  selected: Set<string>,
  locale: 'en' | 'ar',
  setStatus: (s: SyncStatus) => void,
): Promise<void> {
  setStatus('saving');
  const now = new Date().toISOString();
  const picks = Array.from(selected)
    .map((sku) => pieces.find((p) => p.id === sku))
    .filter((p): p is Piece => !!p)
    .map((p) => ({
      sku: p.id,
      brand: p.brand,
      model: p.model,
      retailPrice: p.retail,
      currency: 'AED',
      addedAt: now,
    }));
  try {
    const res = await fetch(`${API_BASE}/api/portfolio/${encodeURIComponent(email)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pieces: picks, locale }),
    });
    if (!res.ok) {
      setStatus('error');
      return;
    }
    setStatus('synced');
  } catch {
    setStatus('error');
  }
}
